import { useCallback, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { ConfirmAction } from '@/components/confirm-action'
import { ApiError } from '@/lib/api'
import { LanguageEditor } from '@/features/i18n/language-editor'
import {
  useLanguages,
  useSaveLanguage,
  type Language,
  type LanguageInput,
} from '@/features/i18n/api'

/**
 * The language dictionary. Also the data source for the consumer app's language
 * picker, which is why `display_name` is the endonym rather than the English
 * name — it is rendered to end users, not only to staff.
 *
 * Disabling is the consequential action here, so it goes through a confirm:
 * an offline language is dropped from the editor and from every translation
 * run, and its translations stop reaching users. The rows themselves survive,
 * so it is reversible — but not obviously so from the switch alone.
 */
export function LanguagesPage() {
  const query = useLanguages()
  const save = useSaveLanguage()
  const [pendingDisable, setPendingDisable] = useState<Language | null>(null)
  const [editing, setEditing] = useState<Language | null>(null)
  const [creating, setCreating] = useState(false)

  /**
   * The whole row goes back on every edit. `LanguageInput` is the full record
   * minus the two immutable fields, so a partial body would blank whatever it
   * omitted — unlike the message endpoint, which is field-level by design
   * (D-11). Different rules on the two endpoints, deliberately: the language
   * dictionary has no cascade to protect.
   *
   * `sort_weight` is the one to watch: it has no column on this screen any more,
   * so if it were dropped from this body a Popular toggle would silently reorder
   * the consumer app's language picker and nothing here would show it.
   */
  const patch = useCallback(
    async (language: Language, changes: Partial<LanguageInput>) => {
      await save.mutateAsync({
        id: language.id,
        input: {
          display_code: language.display_code,
          display_name: language.display_name,
          llm_name: language.llm_name,
          is_popular: language.is_popular,
          is_rtl: language.is_rtl,
          sort_weight: language.sort_weight,
          enabled: language.enabled,
          ...changes,
        },
      })
    },
    [save],
  )

  const setEnabled = useCallback(
    async (language: Language, enabled: boolean) => {
      await patch(language, { enabled })
      toast.success(
        enabled
          ? `${language.display_name} enabled. Existing keys read as untranslated until you run Fill missing.`
          : `${language.display_name} disabled.`,
      )
    },
    [patch],
  )

  const setPopular = useCallback(
    async (language: Language, isPopular: boolean) => {
      try {
        await patch(language, { is_popular: isPopular })
        toast.success(
          isPopular
            ? `${language.display_name} moved into the picker's Popular group.`
            : `${language.display_name} removed from Popular.`,
        )
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : 'Could not update the language.')
      }
    },
    [patch],
  )

  /**
   * Enabling goes straight through; disabling is routed via the confirm below.
   * Errors are caught here rather than inside `setEnabled` so the confirm dialog
   * can surface a failure itself — it rejects to show the message in place.
   */
  const toggle = useCallback(
    async (language: Language, enabled: boolean) => {
      try {
        await setEnabled(language, enabled)
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : 'Could not update the language.')
      }
    },
    [setEnabled],
  )

  const columns = useMemo<ColumnDef<Language, unknown>[]>(
    () => [
      {
        id: 'language',
        header: 'Language',
        cell: ({ row }) => (
          <div>
            <div className="text-[13px] font-semibold">{row.original.display_name}</div>
            <code className="font-mono text-[11px] text-muted-foreground">{row.original.code}</code>
          </div>
        ),
      },
      {
        id: 'llm_name',
        header: 'Sent to the model as',
        cell: ({ row }) => (
          // Spelled out rather than the BCP47 code: handing a model "zh-tw"
          // produces noticeably worse output than "Traditional Chinese".
          <span className="text-[13px] text-muted-foreground">{row.original.llm_name}</span>
        ),
      },
      {
        id: 'flags',
        header: 'Flags',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.is_baseline && <Badge variant="accent">Baseline</Badge>}
            {row.original.is_rtl && <Badge variant="default">RTL</Badge>}
          </div>
        ),
      },
      {
        id: 'popular',
        header: 'Popular',
        enableSorting: false,
        cell: ({ row }) => {
          const language = row.original
          return (
            <Switch
              checked={language.is_popular}
              // The baseline is always in the Popular group — English is the
              // fallback every other language resolves to, so burying it in the
              // long tail of the picker would be actively unhelpful.
              disabled={language.is_baseline || save.isPending}
              aria-label={`${language.display_name} in the Popular group`}
              onCheckedChange={(checked) => void setPopular(language, checked)}
            />
          )
        },
      },
      {
        id: 'enabled',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => {
          const language = row.original
          return (
            <Switch
              checked={language.enabled}
              // The baseline cannot be switched off: English is the source every
              // other language falls back to, and the database keeps exactly one.
              disabled={language.is_baseline || save.isPending}
              aria-label={`${language.display_name} enabled`}
              onCheckedChange={(checked) => {
                if (!checked) setPendingDisable(language)
                else void toggle(language, true)
              }}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setEditing(row.original)}>
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [save.isPending, toggle, setPopular],
  )

  return (
    <>
      <PageHeader
        title="Languages"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Add language
          </Button>
        }
      />

      <Notice>
        This list is also what the consumer app builds its language picker from — grouping, order
        and RTL travel with it, so a change here reaches users without a client release.{' '}
        <b>Rows appear in the order users see them</b>: Popular first, then by a stored weight the
        console does not currently edit.{' '}
        <b>Codes are lowercase</b> (<code>zh-tw</code>), matching the frontend locale folders. A
        mismatched code never errors; it just finds no translations.
      </Notice>

      <Card>
        <DataTable columns={columns} query={query} emptyMessage="No languages configured." />
      </Card>

      <ConfirmAction
        open={pendingDisable !== null}
        onOpenChange={(open) => !open && setPendingDisable(null)}
        title={`Disable ${pendingDisable?.display_name}?`}
        description={
          <>
            It disappears from the translation editor and is skipped by every translation run, and
            users stop being offered it. Existing translations are kept, so enabling it again
            restores them.
          </>
        }
        confirmLabel="Disable"
        destructive
        onConfirm={async () => {
          if (pendingDisable) await setEnabled(pendingDisable, false)
        }}
        onConflict={() => void query.refetch()}
      />

      {creating && <LanguageEditor open onOpenChange={(open) => !open && setCreating(false)} />}

      {editing && (
        // Keyed so switching rows rebuilds the form — `useForm` reads its
        // defaults once, and reusing the instance would show the previous
        // language's values.
        <LanguageEditor
          key={editing.id}
          language={editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </>
  )
}
